import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './Waitlist.module.css';
import deenLogo from './assets/deenroute-logo.png'; // Ensure this path is correct

const GOOGLE_SCRIPT_URL = 'https://script.google.com/macros/s/AKfycbyD-IoIwu6aQtEFh1V1d9pIi2z0RqRPVTvwa4hRmLvzTW-7FaFbcuY21gRo2dV-kDE/exec';
const LOCAL_STORAGE_KEY = 'deenroute_pending_sync';

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function savePending(payload) {
  try {
    const raw = localStorage.getItem(LOCAL_STORAGE_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    arr.push(payload);
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(arr));
  } catch (e) {
    console.error('Failed saving pending payload', e);
  }
}

async function sendToGoogle(payload) {
  try {
    await fetch(GOOGLE_SCRIPT_URL, {
      method: 'POST',
      mode: 'no-cors',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });
    return true;
  } catch (err) {
    return false;
  }
}

export default function WaitlistPage() {
  const navigate = useNavigate();
  const [stage, setStage] = useState(1);
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState('');

  // Survey state
  const [q1, setQ1] = useState('');
  const [q2, setQ2] = useState('');
  const [q3, setQ3] = useState(null);
  const [consent, setConsent] = useState(false);

  const mountedRef = useRef(true);

  // Sync offline data on mount
  useEffect(() => {
    mountedRef.current = true;
    (async function resendPending() {
      try {
        const raw = localStorage.getItem(LOCAL_STORAGE_KEY);
        if (!raw) return;
        const arr = JSON.parse(raw);
        if (!Array.isArray(arr) || arr.length === 0) return;

        const remaining = [];
        for (const item of arr) {
          try {
            const ok = await sendToGoogle(item);
            if (!ok) remaining.push(item);
          } catch (e) {
            remaining.push(item);
          }
        }

        if (remaining.length === 0) {
          localStorage.removeItem(LOCAL_STORAGE_KEY);
        } else {
          localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(remaining));
        }
      } catch (e) {
        // ignore
      }
    })();

    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Toast timer
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(''), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  const handleJoin = async (e) => {
    e && e.preventDefault();
    setEmailError('');

    if (!emailRegex.test(email.trim())) {
      setEmailError('Please enter a valid email');
      return;
    }

    setLoading(true);

    // CRITICAL FIX: Matching Google Script expectations
    const payload = {
      stage: 'started', // Triggers the Welcome Email
      email: email.trim(),
      timestamp: new Date().toISOString(),
    };

    try {
      // Fire and forget (optimistic)
      sendToGoogle(payload).then(ok => {
        if (!ok) {
          savePending(payload);
          setToast("Saved offline. We'll sync automatically.");
        }
      });
    } catch (err) {
      savePending(payload);
      setToast("Saved offline. We'll sync automatically.");
    }

    setLoading(false);
    setStage(2);
  };

  const handleComplete = async () => {
    if (!q1 || !q2 || !q3 || !consent) {
      setToast('Please answer all questions to continue');
      return;
    }

    setLoading(true);

    // CRITICAL FIX: Nesting survey data
    const payload = {
      stage: 'completed',
      email: email.trim(),
      timestamp: new Date().toISOString(),
      survey: {
        q1: q1,
        q2: q2,
        q3: q3,
        consent: consent
      }
    };

    try {
      sendToGoogle(payload).then(ok => {
        if (!ok) {
          savePending(payload);
          setToast("Saved offline. We'll sync automatically.");
        }
      });
    } catch (err) {
      savePending(payload);
      setToast("Saved offline. We'll sync automatically.");
    }

    setLoading(false);
    setStage(3);
  };

  return (
      <div className={styles.page}>
        <div className={styles.container}>
          {deenLogo && <img src={deenLogo} alt="DeenRoute" className={styles.logo} />}

          {/* Stage 1 */}
          <form
              className={`${styles.card} ${stage === 1 ? styles.show : styles.hide}`}
              onSubmit={handleJoin}
              aria-hidden={stage !== 1}
          >
            <h1 className={styles.title}>Islamic Learning, Gamified</h1>
            <p className={styles.subtitle}>Join the waitlist for the mobile launch.</p>

            <label htmlFor="email" className={styles.label}>
              Email Address
            </label>
            <input
                id="email"
                name="email"
                type="email"
                className={styles.input}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@domain.com"
                required
            />
            {emailError && (
                <div className={styles.error} role="alert">{emailError}</div>
            )}

            <button className={styles.primaryButton} type="submit" disabled={loading}>
              {loading ? 'Joining...' : 'Join Waitlist'}
            </button>
          </form>

          {/* Stage 2 */}
          <div
              className={`${styles.card} ${stage === 2 ? styles.show : styles.hide}`}
              aria-hidden={stage !== 2}
          >
            <h2 className={styles.cardTitle}>Help us build for you</h2>

            <div className={styles.question}>
              <div className={styles.qLabel}>Do you use a structured Islamic app?</div>
              <div className={styles.optionsRow}>
                {['No', 'Yes', 'YouTube'].map(opt => (
                    <button
                        key={opt}
                        type="button"
                        className={`${styles.optionButton} ${q1 === opt ? styles.selected : ''}`}
                        onClick={() => setQ1(opt)}
                    >
                      {opt}
                    </button>
                ))}
              </div>
            </div>

            <div className={styles.question}>
              <div className={styles.qLabel}>Biggest frustration currently?</div>
              <div className={styles.optionsRow}>
                {['Boring', 'Unstructured', 'Trust'].map(opt => (
                    <button
                        key={opt}
                        type="button"
                        className={`${styles.optionButton} ${q2 === opt ? styles.selected : ''}`}
                        onClick={() => setQ2(opt)}
                    >
                      {opt}
                    </button>
                ))}
              </div>
            </div>

            <div className={styles.question}>
              <div className={styles.qLabel}>Likelihood to use daily? (1-5)</div>
              <div className={styles.ratingRow}>
                {[1, 2, 3, 4, 5].map((n) => (
                    <button
                        key={n}
                        type="button"
                        className={`${styles.ratingButton} ${q3 === n ? styles.selected : ''}`}
                        onClick={() => setQ3(n)}
                    >
                      {n}
                    </button>
                ))}
              </div>
            </div>

            <label className={styles.consentRow}>
              <input
                  type="checkbox"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  style={{width:'18px', height:'18px', accentColor:'#aec597'}}
              />
              <span className={styles.consentText}>I agree to share my feedback.</span>
            </label>

            <div className={styles.actionsRow}>
              <button className={styles.ghostButton} onClick={() => setStage(1)}>
                Back
              </button>
              <button
                  className={styles.primaryButton}
                  onClick={handleComplete}
                  disabled={loading}
              >
                {loading ? 'Saving...' : 'Complete'}
              </button>
            </div>
          </div>

          {/* Stage 3 */}
          <div className={`${styles.card} ${stage === 3 ? styles.show : styles.hide}`} aria-hidden={stage !== 3}>
            <div className={styles.successIcon}>
              <svg viewBox="0 0 52 52" className={styles.checkSvg}>
                <circle cx="26" cy="26" r="25" fill="none" stroke="#aec597" strokeWidth="2" />
                <path
                    fill="none"
                    stroke="#3c763d"
                    strokeWidth="4"
                    d="M14 27l7 7 17-17"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
              </svg>
            </div>
            <h2 className={styles.cardTitle}>You're a Founding Member!</h2>
            <p className={styles.subtitle}>Check your email for confirmation.</p>
            <div className={styles.actionsRow}>
              <button className={styles.ghostButton} onClick={() => navigate('/')}>
                Done
              </button>
            </div>
          </div>

          {toast && <div className={styles.toast}>{toast}</div>}
        </div>
      </div>
  );
}